// API Configuration
// Automatically detects the correct backend URL based on current hostname

const getBackendUrl = (): string => {
  // Handle SSR or missing window
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:4000';
  }

  const { hostname, origin } = window.location;
  const port = '4000'; // Backend port for local/network development

  // Production: run API on the same origin under /api
  if (hostname === 'tinklas.mokslosala.eu') {
    return origin; // will be https://tinklas.mokslosala.eu
  }

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:${port}`;
  }

  // LAN / other hosts during development
  return `http://${hostname}:${port}`;
};

export const API_BASE_URL = (() => {
  const url = getBackendUrl();
  if (process.env.NODE_ENV === 'development') {
    console.log('API Base URL:', url);
  }
  return url;
})();

// Helper function to get full API URL
export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}/api${endpoint}`;
};

// Common API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  PROFILE: '/auth/profile',
  LOGOUT: '/auth/logout',
  CHANGE_PASSWORD: '/auth/change-password',
  FORCE_CHANGE_PASSWORD: '/auth/force-change-password',
  
  // Lockers
  LOCKERS: '/lockers',

  // Users
  USERS: '/users',
  USER_PROFILE: '/users/profile',
  PENDING_USERS: '/users/pending',
  APPROVE_USER: (userId: string) => `/users/${userId}/approve`,
  REJECT_USER: (userId: string) => `/users/${userId}/reject`,
  UPDATE_USER: (userId: string) => `/users/${userId}`,
  USER_PIN: (userId: string) => `/users/${userId}/pin`,
  USER_UNPIN: (userId: string) => `/users/${userId}/pin`,
  USERS_PINNED: '/users/pinned',
  USER_EXCLUDE_FROM_WORKERS_COUNT: (userId: string) => `/users/${userId}/exclude-from-workers-count`,
  USER_INCLUDE_IN_WORKERS_COUNT: (userId: string) => `/users/${userId}/exclude-from-workers-count`,
  USERS_EXCLUDED_FROM_WORKERS_COUNT: '/users/excluded-from-workers-count',
  USERS_REMOTE_WEEKLY_LIMITS: '/users/remote-weekly-limits',
  USER_REMOTE_WEEKLY_LIMIT: (userId: string) => `/users/${userId}/remote-weekly-limit`,
  USERS_VACATION_14DAY_CONFIRMATIONS: '/users/vacation-14day-confirmations',
  USER_VACATION_14DAY_CONFIRMATION: (userId: string) => `/users/${userId}/vacation-14day-confirmation`,
  
  // Points
  BALANCE: (userId: string) => `/points/balance/${userId}`,
  TRANSACTIONS: (userId: string) => `/points/transactions/${userId}`,
  DISTRIBUTION: '/points/distribution',
  BULK_AWARD: '/points/bulk-award',
  
  // Rewards
  REWARDS: '/rewards',
  
  // Projects
  PROJECTS: '/projects',
  PROJECT_DETAILS: (projectId: string) => `/projects/${projectId}`,
  PROJECT_MEMBERS: (projectId: string) => `/projects/${projectId}/members`,
  PROJECT_DEPARTMENTS: (projectId: string) => `/projects/${projectId}/departments`,
  AVAILABLE_USERS: '/projects/available/users',
  AVAILABLE_DEPARTMENTS: '/projects/available/departments',
  
  // Tasks
  TASKS: '/tasks',
  TASKS_BY_PROJECT: (projectId: string) => `/tasks/project/${projectId}`,
  
  // Governance
  PROPOSALS: '/governance/proposals',
  PROPOSAL_DETAILS: (proposalId: string) => `/governance/proposals/${proposalId}`,
  PROPOSAL_VOTE: (proposalId: string) => `/governance/proposals/${proposalId}/vote`,
  PROPOSAL_STATUS: (proposalId: string) => `/governance/proposals/${proposalId}/status`,
  MY_VOTE: (proposalId: string) => `/governance/proposals/${proposalId}/my-vote`,
  
  // Blog
  BLOG_POSTS: '/blog',
  BLOG_POST: (postId: string) => `/blog/${postId}`,
  BLOG_STATUS: (postId: string) => `/blog/${postId}/status`,
  BLOG_REACTIONS: (postId: string) => `/blog/${postId}/reactions`,
  BLOG_REACTION: (postId: string, emoji: string) => `/blog/${postId}/reactions/${encodeURIComponent(emoji)}`,
  
  // Forms (Vidiniai dokumentai)
  FORMS: '/forms',
  FORM_BY_ID: (id: string) => `/forms/${id}`,
  FORM_DOWNLOAD: (id: string) => `/forms/${id}/download`,
  
  // Infrastructure Requests
  INFRASTRUCTURE: '/infrastructure',
  INFRASTRUCTURE_REQUEST: (requestId: string) => `/infrastructure/${requestId}`,
  INFRASTRUCTURE_ACCEPT: (requestId: string) => `/infrastructure/${requestId}/accept`,
  INFRASTRUCTURE_ASSIGN: (requestId: string) => `/infrastructure/${requestId}/assign`,
  INFRASTRUCTURE_STATUS: (requestId: string) => `/infrastructure/${requestId}/status`,
  INFRASTRUCTURE_DELETE: (requestId: string) => `/infrastructure/${requestId}`,
  INFRASTRUCTURE_MY_REQUESTS: '/infrastructure/my-requests',
  INFRASTRUCTURE_ASSIGNED: '/infrastructure/assigned-to-me',
  
  // Complaints (Skaistykla)
  COMPLAINTS: '/complaints',
  COMPLAINT: (complaintId: string) => `/complaints/${complaintId}`,
  COMPLAINT_ASSIGN: (complaintId: string) => `/complaints/${complaintId}/assign`,
  COMPLAINT_STATUS: (complaintId: string) => `/complaints/${complaintId}/status`,
  COMPLAINT_MESSAGES: (complaintId: string) => `/complaints/${complaintId}/messages`,
  COMPLAINTS_MY_COMPLAINTS: '/complaints/my-complaints',
  COMPLAINTS_ASSIGNED: '/complaints/assigned-to-me',
  
  // Work Schedule
  WORK_SCHEDULE: '/work-schedule',
  WORK_SCHEDULE_DEPARTMENT_VIEW: '/work-schedule/department-view',
  WORK_SCHEDULE_CHANGE_REQUEST: '/work-schedule/change-request',
  WORK_SCHEDULE_CHANGE_REQUESTS: '/work-schedule/change-requests',
  WORK_SCHEDULE_REMOTE_TEMPLATE: '/work-schedule/remote-template',
  WORK_SCHEDULE_DEPARTMENT_TEMPLATES: '/work-schedule/department-templates',
  WORK_SCHEDULE_DEPARTMENT_AUTOFILL: '/work-schedule/department-autofill',
  WORK_SCHEDULE_APPROVE_CHANGE_REQUEST: (id: string) => `/work-schedule/approve-change-request/${id}`,
  WORK_SCHEDULE_REJECT_CHANGE_REQUEST: (id: string) => `/work-schedule/reject-change-request/${id}`,
  WORK_SCHEDULE_LOGS: '/work-schedule/logs',
  WORK_SCHEDULE_HIGHLIGHTS: '/work-schedule/highlights',
  WORK_SCHEDULE_BLUE_MARKS: '/work-schedule/blue-marks',
  
  // Excursions
  EXCURSIONS: '/excursions',
  EXCURSIONS_BY_DATE_RANGE: '/excursions/by-date-range',
  EXCURSIONS_UNASSIGNED_COUNT: '/excursions/unassigned-count',

  // Family / employee tickets
  TICKET_REQUESTS_PENDING_COUNT: '/ticket-requests/pending-count',
  
  // Edukacijos
  EDUKATIONS: '/edukations',
  EDUKATIONS_BY_DATE_RANGE: '/edukations/by-date-range',

  // Planetarium
  PLANETARIUM: '/planetarium',
  PLANETARIUM_BY_DATE_RANGE: '/planetarium/by-date-range',
  PLANETARIUM_STAFF_POOL: '/planetarium/staff-pool',
  PLANETARIUM_STAFFING_WARNINGS: '/planetarium/staffing-warnings',
  PLANETARIUM_COLLEAGUES: '/planetarium/colleagues/available',
  PLANETARIUM_USERS_FOR_POOL: '/planetarium/users-for-pool',
  PLANETARIUM_PROGRAMS: '/planetarium/programs',
  PLANETARIUM_PROGRAM: (id: string) => `/planetarium/programs/${id}`,
  PLANETARIUM_QUICK_DAY: '/planetarium/quick-day',
  PLANETARIUM_POPULATE_MONTH: '/planetarium/populate-month',
  PLANETARIUM_REPLACE_DAY: '/planetarium/replace-day',
  PLANETARIUM_ASSIGN_BULK: '/planetarium/assign-bulk',
  PLANETARIUM_LOGS: '/planetarium/logs',
  PLANETARIUM_SESSION: (id: string) => `/planetarium/${id}`,
  PLANETARIUM_PRIVATE_REQUESTS: '/planetarium/private-requests',
  PLANETARIUM_PRIVATE_REQUESTS_PENDING_COUNT: '/planetarium/private-requests/pending-count',
  PLANETARIUM_PRIVATE_REQUEST: (sessionId: string) =>
    `/planetarium/sessions/${sessionId}/private-request`,
  PLANETARIUM_PROGRAM_CHANGE_REQUEST: (sessionId: string) =>
    `/planetarium/sessions/${sessionId}/program-change-request`,
  PLANETARIUM_PRIVATE_REQUEST_ACCEPT: (id: string) =>
    `/planetarium/private-requests/${id}/accept`,
  PLANETARIUM_PRIVATE_REQUEST_DENY: (id: string) => `/planetarium/private-requests/${id}/deny`,
  EDUKATION_PARTICIPANTS: (id: string) => `/edukations/${id}/participants`,
  EDUKATION_PARTICIPANTS_UNCONFIRM: (id: string) => `/edukations/${id}/participants/unconfirm`,
  EXHIBITION_LOGS: '/exhibition/logs',
  
  // Storage
  STORAGE: '/storage',
  STORAGE_UPLOAD: '/storage/upload',
  STORAGE_ITEMS: '/storage/items',
  STORAGE_ITEMS_BY_CATEGORY: (category: string) => `/storage/items/${category}`,
  STORAGE_SUPPLIERS_BY_CATEGORY: (category: string) => `/storage/items/${category}/suppliers`,
  STORAGE_TRANSACTIONS: '/storage/transactions',
  STORAGE_CLEAR_TRANSACTIONS: '/storage/transactions',
  STORAGE_MONTHLY_RESTOCKS: '/storage/transactions/monthly-restocks',
  STORAGE_SALES_REPORT: '/storage/reports/sales',
  STORAGE_STATS: '/storage/stats',
  STORAGE_CHECKOUT: '/storage/checkout',
  STORAGE_RESTOCK: '/storage/restock',
  STORAGE_RESTOCK_BATCH: '/storage/restock-batch',
  STORAGE_RESTOCK_LOG: '/storage/restock-log',
  STORAGE_RESTOCK_LOG_ITEMS: (batchId: string) => `/storage/restock-log/${batchId}/items`,
  STORAGE_SALES_REGISTRATION: '/storage/sales-registration',
  STORAGE_SALES_REGISTRATION_LOG: '/storage/sales-registration-log',
  STORAGE_SUPPLIER_RETURN: '/storage/supplier-return',
  STORAGE_SUPPLIER_RETURN_LOG: '/storage/supplier-return-log',
  STORAGE_SUPPLIER_RETURN_LOG_ITEMS: (batchId: string) => `/storage/supplier-return-log/${batchId}/items`,
  STORAGE_STORE_TRANSFER: '/storage/store-transfer',
  STORAGE_STORE_TRANSFER_LOG: '/storage/store-transfer-log',
  STORAGE_STORE_TRANSFER_LOG_ITEMS: (batchId: string) => `/storage/store-transfer-log/${batchId}/items`,

  // Month Lock Management
  MONTH_STATUS: (year: number, month: number, department?: string | null) => {
    const base = `/work-schedule/month-status/${year}/${month}`;
    if (department) {
      return `${base}?department=${encodeURIComponent(department)}`;
    }
    return base;
  },
  MONTH_LOCKS: '/work-schedule/month-locks',
  MONTH_LOCK: '/work-schedule/month-lock',
  MONTH_UNLOCK: '/work-schedule/month-unlock',
  MONTH_LOCK_DEPARTMENTS: '/work-schedule/month-lock-departments',
  MONTH_STATUSES: '/work-schedule/month-statuses',
  MONTH_OPEN_SCHEDULING: '/work-schedule/open-scheduling',
  MONTH_CLOSE_SCHEDULING: '/work-schedule/close-scheduling',
  SCHEDULING_AUTO_CLOSE_DAYS: '/work-schedule/scheduling-auto-close-days',
  SCHEDULING_CLOSE_ANNOUNCEMENT: '/work-schedule/scheduling-close-announcement',
  SCHEDULING_CLOSE_ANNOUNCEMENT_DISMISS: '/work-schedule/scheduling-close-announcement/dismiss',
  SCHEDULING_CLOSE_ANNOUNCEMENT_TRIGGER: '/work-schedule/scheduling-close-announcement/trigger',
  SCHEDULING_CLOSE_ANNOUNCEMENT_MESSAGE: '/work-schedule/scheduling-close-announcement-message',
  SCHEDULING_AUTO_FREEZE: '/work-schedule/scheduling-auto-freeze',
  
  // Notifications
  NOTIFICATIONS: '/notifications',
  
  // Exhibition
  EXHIBITION: '/exhibition',
  EXHIBITION_ADMIN: '/exhibition/admin',
  EXHIBITION_ACTIVITY_REPORT: '/exhibition/reports/activity',
  EXHIBITION_PANEL: (panelId: string) => `/exhibition/${panelId}`,
  EXHIBITION_UPLOAD: '/exhibition/upload',

  // Schemas
  SCHEMAS: '/schemas',
  SCHEMAS_ADMIN: '/schemas/admin',
  SCHEMAS_PANEL: (panelId: string) => `/schemas/${panelId}`,
  SCHEMAS_UPLOAD: '/schemas/upload',
  
  // Contacts
  CONTACTS: '/contacts',
  CONTACTS_PARTNERS: '/contacts/partners',
  CONTACT_PARTNER: (id: string) => `/contacts/partners/${id}`,
  
  // Work Plans
  WORK_PLANS: '/work-plans',
  WORK_PLAN: (id: string) => `/work-plans/${id}`,
  WORK_PLANS_REORDER: '/work-plans/reorder',
  WORK_PLANS_ASSIGNED_TASKS: '/work-plans/assigned-tasks',

  // Staff plans (onboarding / qualification / growth)
  STAFF_PLANS_TEMPLATES: '/staff-plans/templates',
  STAFF_PLANS_TEMPLATE: (id: string) => `/staff-plans/templates/${id}`,
  STAFF_PLANS_SECTIONS: '/staff-plans/sections',
  STAFF_PLANS_SECTION: (id: string) => `/staff-plans/sections/${id}`,
  STAFF_PLANS_TASKS: '/staff-plans/tasks',
  STAFF_PLANS_TASK: (id: string) => `/staff-plans/tasks/${id}`,
  STAFF_PLANS_WORKERS: '/staff-plans/workers',
  STAFF_PLANS_ASSIGNMENTS: '/staff-plans/assignments',
  STAFF_PLANS_ASSIGNMENT: (id: string) => `/staff-plans/assignments/${id}`,
  STAFF_PLANS_ASSIGNMENT_TASK: (assignmentId: string, taskId: string) =>
    `/staff-plans/assignments/${assignmentId}/tasks/${taskId}`,
  STAFF_PLANS_SECTION_COMPLETE: (assignmentId: string, sectionId: string) =>
    `/staff-plans/assignments/${assignmentId}/sections/${sectionId}/complete`,
  STAFF_PLANS_LOGS: '/staff-plans/logs',
  
  // Health check
  HEALTH: '/health',
};

export default API_BASE_URL; 